import { siteDay } from "@/lib/site-time";
/**
 * Gedeelde definities voor Real User Monitoring van Core Web Vitals.
 *
 * Wordt zowel in de browser (verzameling) als op de server (validatie,
 * aggregatie) gebruikt. Bevat geen browser- of server-specifieke API's op
 * moduleniveau, zodat het bestand aan beide kanten veilig geladen kan worden.
 */

export const VITALS_SITE_ID = "gopro";

export const METRIC_NAMES = ["LCP", "INP", "CLS", "FCP", "TTFB"] as const;
export type MetricName = (typeof METRIC_NAMES)[number];

export const CORE_METRICS: MetricName[] = ["LCP", "INP", "CLS"];

export type Rating = "good" | "needs-improvement" | "poor";
export type DeviceType = "mobile" | "desktop";

export const NAVIGATION_TYPES = [
  "navigate",
  "reload",
  "back-forward",
  "back-forward-cache",
  "prerender",
  "restore",
] as const;
export type NavigationType = (typeof NAVIGATION_TYPES)[number];

/** Officiële Google-drempels (goed <= good, matig <= poor, daarboven slecht). */
export const THRESHOLDS: Record<MetricName, { good: number; poor: number }> = {
  LCP: { good: 2500, poor: 4000 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
};

/**
 * Extra fijnmazige drempels voor het dashboard. "excellent" en "very-good"
 * zijn strakkere varianten van "good" zodat de sprong van "matig" naar
 * "uitmuntend" niet te groot is.
 */
export const DISPLAY_THRESHOLDS: Record<MetricName, { excellent: number; veryGood: number }> = {
  LCP: { excellent: 1200, veryGood: 1500 },
  INP: { excellent: 80, veryGood: 120 },
  CLS: { excellent: 0.03, veryGood: 0.05 },
  FCP: { excellent: 800, veryGood: 1200 },
  TTFB: { excellent: 300, veryGood: 500 },
};

export type DisplayRating = "excellent" | "very-good" | "good" | "needs-improvement" | "poor";

/**
 * Officiële namen en uitleg per metriek, zoals Google ze hanteert in
 * CrUX/Search Console. Wordt in het admin-dashboard getoond zodat de
 * afkortingen niet uit het hoofd geleerd hoeven te worden.
 */
export const METRIC_INFO: Record<
  MetricName,
  { fullName: string; description: string; core: boolean }
> = {
  LCP: {
    fullName: "Largest Contentful Paint",
    description:
      "Hoe snel het grootste zichtbare element (beeld of tekstblok) geladen is. Google's maat voor laadsnelheid.",
    core: true,
  },
  INP: {
    fullName: "Interaction to Next Paint",
    description:
      "Hoe snel de pagina reageert op kliks en toetsaanslagen, gemeten over de hele sessie. Google's maat voor interactiviteit (opvolger van FID).",
    core: true,
  },
  CLS: {
    fullName: "Cumulative Layout Shift",
    description:
      "Hoeveel de layout onverwacht verschuift tijdens het laden en gebruiken. Google's maat voor visuele stabiliteit. Dimensieloos.",
    core: true,
  },
  FCP: {
    fullName: "First Contentful Paint",
    description:
      "Hoe snel het eerste stukje inhoud (tekst of beeld) op het scherm verschijnt. Diagnostisch, geen Core Web Vital.",
    core: false,
  },
  TTFB: {
    fullName: "Time to First Byte",
    description:
      "Hoe snel de server het eerste stukje HTML levert. Diagnostisch, geen Core Web Vital.",
    core: false,
  },
};

/** Waarschuwingsdrempel voor de latere alerting: 80% van de "goed"-grens. */
export const ALERT_THRESHOLDS: Record<MetricName, number> = {
  LCP: THRESHOLDS.LCP.good * 0.8,
  INP: THRESHOLDS.INP.good * 0.8,
  CLS: THRESHOLDS.CLS.good * 0.8,
  FCP: THRESHOLDS.FCP.good * 0.8,
  TTFB: THRESHOLDS.TTFB.good * 0.8,
};

/** Onder dit aantal metingen is een p75 statistisch niet te vertrouwen. */
export const MIN_RELIABLE_SAMPLES = 50;

/** Bovengrenzen tegen onzin-/misbruikwaarden in de databank. */
export const MAX_VALUE: Record<MetricName, number> = {
  LCP: 120_000,
  INP: 120_000,
  CLS: 100,
  FCP: 120_000,
  TTFB: 120_000,
};

export function isMetricName(value: unknown): value is MetricName {
  return typeof value === "string" && (METRIC_NAMES as readonly string[]).includes(value);
}

export function rateMetric(name: MetricName, value: number): Rating {
  const t = THRESHOLDS[name];
  if (value <= t.good) return "good";
  if (value <= t.poor) return "needs-improvement";
  return "poor";
}

/** Fijnmazige 5-klasse weergave-rating voor het admin-dashboard. */
export function displayRating(name: MetricName, value: number): DisplayRating {
  const t = THRESHOLDS[name];
  const d = DISPLAY_THRESHOLDS[name];
  if (value <= d.excellent) return "excellent";
  if (value <= d.veryGood) return "very-good";
  if (value <= t.good) return "good";
  if (value <= t.poor) return "needs-improvement";
  return "poor";
}

/** Eenheid waarin de metriek getoond wordt. CLS is dimensieloos. */
export function formatMetric(name: MetricName, value: number | null): string {
  if (value === null || Number.isNaN(value)) return "—";
  if (name === "CLS") return value.toFixed(3);
  if (value >= 1000) return `${(value / 1000).toFixed(2)} s`;
  return `${Math.round(value)} ms`;
}

/** p-percentiel volgens de "nearest rank"-methode, zoals CrUX het doet. */
export function percentile(values: number[], p: number): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = Math.ceil((p / 100) * sorted.length);
  const index = Math.min(sorted.length - 1, Math.max(0, rank - 1));
  return sorted[index] ?? null;
}

const BOT_RE =
  /bot|crawl|spider|slurp|headless|lighthouse|pagespeed|preview|monitor|pingdom|gtmetrix|ahrefs|semrush|facebookexternalhit|whatsapp|embedly|phantom|puppeteer|playwright|curl|wget|python-requests|node-fetch/i;

/** Grove botfilter op user-agent. Bewust ruim: liever een meting te weinig. */
export function isLikelyBot(userAgent: string | null | undefined): boolean {
  if (!userAgent) return true;
  return BOT_RE.test(userAgent);
}

const MOBILE_RE = /android|iphone|ipod|iemobile|blackberry|opera mini|mobile safari|windows phone/i;

/** Apparaatafleiding uit de user-agent; tablets tellen als desktop-viewport. */
export function deviceFromUserAgent(userAgent: string | null | undefined): DeviceType {
  if (!userAgent) return "desktop";
  return MOBILE_RE.test(userAgent) ? "mobile" : "desktop";
}

/** Pathname zonder querystring, hash of trailing slash (behalve de root). */
export function normalizeRoute(input: string): string {
  let path = (input || "/").split("?")[0]!.split("#")[0]!;
  if (!path.startsWith("/")) path = `/${path}`;
  if (path.length > 1) path = path.replace(/\/+$/, "") || "/";
  return path.slice(0, 300);
}

/** Month of the Belgian calendar. */
export function yearMonth(date: Date): string {
  return siteDay(date).slice(0, 7);
}

/** Eén meting zoals de browser ze naar `/api/public/vitals` stuurt. */
export type VitalsBeaconMetric = {
  name: MetricName;
  value: number;
  route: string;
  navigationType: string;
  attribution: Record<string, unknown>;
};

export type VitalsBeacon = {
  pageLoadId: string;
  deviceType: DeviceType;
  connectionType: string | null;
  metrics: VitalsBeaconMetric[];
};
